var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/header/PageHeader";
import { Button, Box, useMediaQuery, IconButton, Typography, Switch, Avatar, Chip, Tooltip, useTheme, alpha, ClickAwayListener, } from "@mui/material";
import { Add, Trash, Edit, SearchNormal } from "iconsax-react";
import { AddEditUserDialog } from "./AddEditUserDialog";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { useCreateUserMutation, useGetUsersSummaryQuery, useUpdateUserStatusMutation, useDeleteUserMutation, useUpdateUserMutation, useGetAllCompaniesQuery, } from "../../../features/projects/projectsApi";
import { useSnackbar } from "notistack";
import { useAuth } from "../../../auth/useAuth";
import { useCompany } from "../../../context/CompanyContext";
import { usePermissions } from "../../../context/PermissionContext";
import { FilteredInput } from "@ui/components/inputs";
const CompaniesCell = ({ companies }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const handleTooltipClose = () => {
        setOpen(false);
    };
    const handleTooltipOpen = () => {
        setOpen(true);
    };
    if (isMobile) {
        return (React.createElement(ClickAwayListener, { onClickAway: handleTooltipClose },
            React.createElement("div", null,
                React.createElement(Tooltip, { PopperProps: {
                        disablePortal: true,
                    }, onClose: handleTooltipClose, open: open, disableFocusListener: true, disableHoverListener: true, disableTouchListener: true, title: companies.join(", ") },
                    React.createElement(Chip, { onClick: handleTooltipOpen, label: companies.length, size: "small", variant: "soft", color: "primary" })))));
    }
    return (React.createElement(Tooltip, { title: companies.join(", ") },
        React.createElement(Chip, { label: companies.length, size: "small", variant: "soft", color: "primary" })));
};
export const Users = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const { account } = useAuth();
    const { companies } = useCompany();
    const { canRead, canCreate, canUpdate, canDelete } = usePermissions();
    const { data: allCompanies } = useGetAllCompaniesQuery();
    const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
    const [updateUserStatus] = useUpdateUserStatusMutation();
    const [deleteUser] = useDeleteUserMutation();
    const [updateUser] = useUpdateUserMutation();
    const { data: usersSummary, isLoading, refetch } = useGetUsersSummaryQuery();
    const { enqueueSnackbar } = useSnackbar();
    // Map API data to User interface
    const users = (usersSummary || [])
        .filter((user) => user.id) // Only include users with valid id
        .map((user) => {
        // Map company IDs to company names using all companies
        const userCompanyNames = (user.companyIds || [])
            .map((companyId) => {
            const company = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.find((c) => c.entiteitId.toLowerCase() === companyId.toLowerCase());
            return company === null || company === void 0 ? void 0 : company.entiteitNaam;
        })
            .filter((name) => name !== undefined);
        return {
            id: user.id,
            avatar: "/assets/avatars/avatar-default.jpg",
            name: user.username || user.userEmail.split("@")[0],
            email: user.userEmail,
            companies: userCompanyNames,
            status: user.isActive,
            roleId: user.roleId,
            roleName: user.roleName,
        };
    });
    const createUserColumns = (onEdit, onDelete, onStatusChange) => [
        {
            field: "name",
            headerName: "Naam",
            flex: 2,
            minWidth: 250,
            renderCell: (params) => (React.createElement(Box, { display: "flex", alignItems: "center", gap: 2 },
                React.createElement(Avatar, { src: params.row.avatar, alt: params.row.name, sx: {
                        width: 40,
                        height: 40,
                        bgcolor: theme.palette.grey[300],
                        color: theme.palette.grey[900],
                    } }),
                React.createElement(Box, null,
                    React.createElement(Typography, { variant: "body1" }, params.row.name)))),
        },
        {
            field: "email",
            headerName: "E-mail",
            flex: 1.5,
            minWidth: 150,
            renderCell: (params) => (React.createElement(Typography, { variant: "body2", color: "text.secondary" }, params.row.email)),
        },
        {
            field: "companies",
            headerName: "Bedrijven",
            flex: 1.5,
            minWidth: 150,
            renderCell: (params) => {
                const companyCount = params.row.companies.length;
                return (React.createElement(Chip, { label: companyCount, size: "small", variant: "outlined", color: "primary" }));
            },
        },
        {
            field: "roleName",
            headerName: "Rol",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (React.createElement(Typography, { variant: "body2", color: "text.secondary" }, params.row.roleName || "—")),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                var _a;
                const isCurrentUser = params.row.email.toLowerCase() === ((_a = account === null || account === void 0 ? void 0 : account.username) === null || _a === void 0 ? void 0 : _a.toLowerCase());
                return (React.createElement(Box, { display: "flex", alignItems: "center", gap: 1 },
                    React.createElement(Tooltip, { title: isCurrentUser ? "U kunt uw eigen status niet wijzigen" : "", arrow: true },
                        React.createElement("span", null,
                            React.createElement(Switch, { checked: params.row.status, onChange: (event, checked) => onStatusChange(params.row, checked), size: "small", color: "primary", disabled: !canUpdate("Users") || isCurrentUser })))));
            },
        },
        {
            field: "actions",
            headerName: "Acties",
            flex: 1,
            minWidth: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                var _a;
                const isCurrentUser = params.row.email.toLowerCase() === ((_a = account === null || account === void 0 ? void 0 : account.username) === null || _a === void 0 ? void 0 : _a.toLowerCase());
                return (React.createElement(Box, { display: "flex" },
                    canUpdate("Users") && (React.createElement(Tooltip, { title: isCurrentUser ? "U kunt uw eigen account niet bewerken" : "", arrow: true },
                        React.createElement("span", null,
                            React.createElement(IconButton, { size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onEdit(params.row);
                                }, disabled: isCurrentUser, sx: {
                                    border: "none",
                                    color: isCurrentUser ? "action.disabled" : "primary.main",
                                    "&:hover": {
                                        backgroundColor: isCurrentUser
                                            ? "transparent"
                                            : alpha(useTheme().palette.primary.main, 0.08),
                                    },
                                } },
                                React.createElement(Edit, { size: 20 }))))),
                    canDelete("Users") && (React.createElement(Tooltip, { title: isCurrentUser
                            ? "U kunt uw eigen account niet verwijderen"
                            : "", arrow: true },
                        React.createElement("span", null,
                            React.createElement(IconButton, { size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onDelete(params.row);
                                }, disabled: isCurrentUser, sx: {
                                    color: isCurrentUser ? "action.disabled" : "error.main",
                                    border: "none",
                                    "&:hover": {
                                        backgroundColor: isCurrentUser
                                            ? "transparent"
                                            : alpha(useTheme().palette.error.main, 0.08),
                                    },
                                } },
                                React.createElement(Trash, { size: 20 })))))));
            },
        },
    ];
    // Add these missing handlers
    const handleEditUser = (user) => {
        setEditUser(user);
        setNewUserDialogOpen(true);
    };
    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setDeleteConfirmOpen(true);
    };
    const handleStatusChange = (user, newStatus) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const modifiedBy = (account === null || account === void 0 ? void 0 : account.username) || "";
            yield updateUserStatus({
                userId: user.id,
                body: {
                    status: newStatus,
                    modifiedBy,
                },
            }).unwrap();
            // Refetch to update the UI
            refetch();
            enqueueSnackbar("Gebruikersstatus succesvol bijgewerkt", {
                variant: "success",
            });
        }
        catch (error) {
            enqueueSnackbar("Fout bij het bijwerken van gebruikersstatus", {
                variant: "error",
            });
        }
    });
    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);
    // Filter users based on search term
    const filteredUsers = useMemo(() => {
        if (!debouncedSearchTerm)
            return users;
        const searchLower = debouncedSearchTerm.toLowerCase();
        return users.filter((user) => user.name.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.companies.some((company) => company.toLowerCase().includes(searchLower)));
    }, [users, debouncedSearchTerm]);
    // Local pagination state for the DataGrid
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    // Clamp page when the filteredUsers length or pageSize changes
    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredUsers.length / pageSize) - 1);
        if (page > maxPage) {
            setPage(0);
        }
    }, [filteredUsers.length, pageSize]);
    const handleAddUser = (newUserData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        // Capture editUser before it gets cleared
        const userToEdit = editUser;
        if (userToEdit) {
            // Update existing user
            try {
                const userEmail = newUserData.email;
                const username = newUserData.name;
                const modifiedBy = (account === null || account === void 0 ? void 0 : account.username) || "";
                // Get company IDs for all selected companies
                const companyIds = newUserData.companies
                    .map((companyName) => {
                    const company = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.find((c) => c.entiteitNaam === companyName);
                    return company === null || company === void 0 ? void 0 : company.entiteitId;
                })
                    .filter((id) => id !== undefined);
                if (companyIds.length === 0) {
                    enqueueSnackbar("Geen geldige bedrijven gevonden", {
                        variant: "error",
                    });
                    return;
                }
                const requestBody = {
                    email: userToEdit.email,
                    body: {
                        companyIds,
                        userEmail,
                        username,
                        isActive: newUserData.status,
                        isDeleted: false,
                        modifiedBy,
                        roleId: newUserData.roleId,
                    },
                };
                yield updateUser(requestBody).unwrap();
                // Refetch the users list from API
                refetch();
                setEditUser(null);
                enqueueSnackbar("Gebruiker succesvol bijgewerkt", {
                    variant: "success",
                });
            }
            catch (error) {
                enqueueSnackbar("Fout bij het bijwerken van gebruiker", {
                    variant: "error",
                });
            }
        }
        else {
            // Add new user - call API with all selected companies
            try {
                const userEmail = newUserData.email;
                const username = newUserData.name;
                const createdBy = (account === null || account === void 0 ? void 0 : account.username) || "";
                // Get company IDs for all selected companies
                const companyIds = newUserData.companies
                    .map((companyName) => {
                    const company = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.find((c) => c.entiteitNaam === companyName);
                    return company === null || company === void 0 ? void 0 : company.entiteitId;
                })
                    .filter((id) => id !== undefined);
                // Create user with all companies in one call
                yield createUser({
                    companyIds,
                    userEmail,
                    username,
                    createdBy,
                    roleId: newUserData.roleId,
                }).unwrap();
                // Refetch the users list from API
                refetch();
                enqueueSnackbar("Gebruiker succesvol aangemaakt", {
                    variant: "success",
                });
            }
            catch (error) {
                // Check if error is due to duplicate user/email
                const isDuplicate = (error === null || error === void 0 ? void 0 : error.status) === 409 ||
                    ((_b = (_a = error === null || error === void 0 ? void 0 : error.data) === null || _a === void 0 ? void 0 : _a.message) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes("already exists")) ||
                    ((_d = (_c = error === null || error === void 0 ? void 0 : error.data) === null || _c === void 0 ? void 0 : _c.message) === null || _d === void 0 ? void 0 : _d.toLowerCase().includes("duplicate")) ||
                    ((_f = (_e = error === null || error === void 0 ? void 0 : error.data) === null || _e === void 0 ? void 0 : _e.message) === null || _f === void 0 ? void 0 : _f.toLowerCase().includes("bestaat al"));
                enqueueSnackbar(isDuplicate
                    ? "Gebruiker bestaat al"
                    : "Fout bij het aanmaken van gebruiker", { variant: "error" });
            }
        }
    });
    const confirmDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (userToDelete) {
            try {
                const modifiedBy = (account === null || account === void 0 ? void 0 : account.username) || "";
                yield deleteUser({
                    userId: userToDelete.id,
                    modifiedBy,
                }).unwrap();
                // Refetch to update the UI
                refetch();
                setDeleteConfirmOpen(false);
                setUserToDelete(null);
                enqueueSnackbar("Gebruiker succesvol verwijderd", {
                    variant: "success",
                });
            }
            catch (error) {
                setDeleteConfirmOpen(false);
                setUserToDelete(null);
                enqueueSnackbar("Fout bij het verwijderen van gebruiker", {
                    variant: "error",
                });
            }
        }
    });
    const handleClearSearch = () => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
    };
    const columns = useMemo(() => createUserColumns(handleEditUser, handleDeleteUser, handleStatusChange), [canUpdate, canDelete]);
    return (React.createElement(Box, null,
        React.createElement(PageHeader, { startActions: React.createElement(FilteredInput, { placeholder: "Zoeken", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), endIcon: React.createElement(SearchNormal, { size: 20, color: "currentColor" }) }), actions: React.createElement(React.Fragment, null,
                !isMobile && (React.createElement(React.Fragment, null, canCreate("Users") && (React.createElement(Button, { variant: "contained", color: "tertiary", size: "large", startIcon: React.createElement(Add, { size: 20 }), onClick: () => {
                        setEditUser(null);
                        setNewUserDialogOpen(true);
                    } }, "Nieuwe gebruiker")))),
                isMobile && canCreate("Users") && (React.createElement(IconButton, { color: "primary", sx: {
                        backgroundColor: theme.palette.tertiary[40],
                        color: "white",
                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },
                    }, onClick: () => {
                        setEditUser(null);
                        setNewUserDialogOpen(true);
                    } },
                    React.createElement(Add, { size: 24 })))) }),
        !canRead("Users") ? (React.createElement(Box, { sx: { p: 4, textAlign: "center" } },
            React.createElement(Typography, { variant: "h6", color: "error" }, "U heeft geen toegang tot de gebruikersmodule"))) : (React.createElement(Box, { sx: {
                height: isMobile ? "calc(100dvh - 145px)" : "calc(100dvh - 190px)",
                overflow: "auto",
                pb: 1,
            } },
            React.createElement(PrimaryGrid, { loading: isLoading, rows: filteredUsers, columns: columns, getRowId: (row) => row.id, pagination: true, paginationMode: "client", paginationModel: { page, pageSize }, onPaginationModelChange: ({ page: newPage, pageSize: newPageSize, }) => {
                    setPage(newPage);
                    setPageSize(newPageSize);
                }, pageSizeOptions: [5, 10, 25], rowCount: filteredUsers.length, heightVariant: "full", sx: {
                    "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                    },
                }, gridHeight: "calc(100dvh - 277px)" }))),
        React.createElement(AddEditUserDialog, { open: newUserDialogOpen, onClose: () => {
                setNewUserDialogOpen(false);
                setEditUser(null);
            }, onSave: handleAddUser, editUser: editUser, availableCompanies: (allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.map((c) => c.entiteitNaam)) || [] }),
        React.createElement(ConfirmDialog, { actionText: "Verwijderen", screenName: "Gebruiker", isOpen: deleteConfirmOpen, onClose: () => setDeleteConfirmOpen(false), onConfirm: confirmDelete })));
};
